/**
 * `docsManifestDto.ts` — wire shape of the account's **document manifest**.
 *
 * A Campfire document has **no Dynamo row of its own**. It is four artifacts,
 * all customer-scoped, plus one attribute on the existing project row:
 *
 * | # | Artifact | S3 key (under `account/{customerId}/projectdata/`) |
 * | - | -------- | -------------------------------------------------- |
 * | 1 | the file | `docs/{fileName}` |
 * | 2 | this manifest | `docs/index.json` |
 * | 3 | PDF thumbnail | `docs/thumbnails/{fileName}.png` (`.png` appended to the FULL name, `.pdf` included) |
 * | 4 | OpenAI vector-store file | one store per customer, id at `vectorstore/vector_store.json` |
 *
 * …and `ProjectData.DocumentStoragePaths` (Dynamo `L` of `S` on the project
 * row) as the project↔doc join.
 *
 * The schema mirrors Unity's `AssetLibraryViewModel.DocsManifest` (`:31-54`),
 * written with Newtonsoft at default settings — so the JSON keys are
 * **PascalCase**, and the AI-side reader (`AIContextEngineModel.KnowledgeTransport.cs:839-856`,
 * `System.Text.Json`, case-SENSITIVE by default) depends on that exactly.
 * Unity carries two DTOs for the same file (the AI one is a narrower subset);
 * the web keeps ONE, consumed by both `ProjectDocsModel` and the knowledge
 * transport.
 *
 * Unknown keys pass through `[k: string]: unknown` so a newer Unity build's
 * fields survive a web read-modify-write of the manifest — the same rule
 * `assetDtoToItem` / `snapshotDtoToItem` follow for Dynamo rows.
 */

/** Unity `k_docsFolder`. */
export const DOCS_FOLDER = "docs";

/** Unity `k_docsManifestStoragePath` — customer-scoped storage path. */
export const DOCS_MANIFEST_STORAGE_PATH = `${DOCS_FOLDER}/index.json`;

/** Unity `CustomerVectorStoreModel.VectorStoreSettingsStoragePath`. */
export const VECTOR_STORE_SETTINGS_STORAGE_PATH = "vectorstore/vector_store.json";

/**
 * Web-only workflow vector-sync index.
 *
 * OpenAI's "list vector store files" endpoint cannot filter by custom
 * attributes, so the web keeps the ids it attached for one workflow here and
 * reconciles that scoped set on `workflow.yaml` changes.
 */
export function workflowVectorManifestStoragePath(workflowId: string): string {
  return `vectorstore/workflows/${encodeURIComponent(workflowId)}.json`;
}

/**
 * Unity `AssetLibraryViewModel.k_docExtensions` (`:57-65`), verbatim and in
 * order: common document formats first, then the code/text formats OpenAI's
 * file API accepts.
 */
export const DOC_EXTENSIONS: ReadonlyArray<string> = [
  // common document formats
  "pdf",
  "doc",
  "docx",
  "ppt",
  "pptx",
  "xls",
  "xlsx",
  "txt",
  "rtf",
  "md",
  "csv",
  // code and text formats for vector/AWS storage
  "c",
  "cpp",
  "cs",
  "css",
  "go",
  "html",
  "java",
  "js",
  "json",
  "php",
  "py",
  "rb",
  "sh",
  "tex",
  "ts",
];

export interface DocsProjectRefDto {
  CustomerId?: string;
  ProjectId?: string;
  [k: string]: unknown;
}

export interface DocsManifestEntryDto {
  FileName?: string;
  StoragePath?: string;
  FileSize?: number;
  UserTag?: string;
  /**
   * Unity writes `DateTime.UtcNow.ToString(CultureInfo.InvariantCulture)` —
   * e.g. `"08/06/2026 14:03:22"`, **not** ISO-8601 — and reads it back with
   * `DateTime.TryParse(..., InvariantCulture)`. `Date.parse` handles that
   * US-format string, and ISO too, so {@link parseDocLastModified} accepts
   * both and the web writes the same InvariantCulture shape Unity does.
   */
  LastModifiedUtc?: string;
  UsedInProjects?: DocsProjectRefDto[];
  [k: string]: unknown;
}

export interface DocsManifestDto {
  Version?: number;
  Docs?: DocsManifestEntryDto[];
  [k: string]: unknown;
}

/** Unity `$"{k_docsFolder}/{fileName}"`. */
export function docStoragePath(fileName: string): string {
  return `${DOCS_FOLDER}/${fileName}`;
}

/**
 * The inverse of `docStoragePath` — `docs/Robodog Manual.pdf` -> the bare file
 * name. It lives here beside its counterpart so the `docs/` prefix is known in
 * ONE place; a caller that strips the folder itself is a second definition
 * waiting to drift.
 *
 * A path carrying no folder segment is returned unchanged rather than emptied:
 * `StoragePath` is written by both clients, and a bare file name is a plausible
 * value there.
 */
export function docFileNameFromStoragePath(storagePath: string): string {
  const normalized = normalizeDocStoragePath(storagePath);
  if (!normalized) return "";
  const slash = normalized.lastIndexOf("/");
  return slash >= 0 ? normalized.slice(slash + 1) : normalized;
}

/**
 * Unity `GetPdfThumbnailStoragePath` — `PathTemplates.k_thumbnailPathTemplate`
 * (`"{0}/thumbnails/{1}"`) with folder `docs` and name `{fileName}.png`.
 * Note the `.png` is appended to the WHOLE file name, extension included:
 * `report.pdf` → `docs/thumbnails/report.pdf.png`.
 */
export function docThumbnailStoragePath(fileName: string): string {
  return `${DOCS_FOLDER}/thumbnails/${fileName}.png`;
}

/** Unity `NormalizeDocStoragePath`. */
export function normalizeDocStoragePath(storagePath: string | undefined | null): string | null {
  const trimmed = storagePath?.trim();
  return trimmed ? trimmed : null;
}

/**
 * Unity `BuildDocStoragePathSet` — an `OrdinalIgnoreCase` `HashSet<string>`.
 * The web spelling is a lower-cased `Set`; always probe it with a lower-cased
 * key, and keep the LITERAL path for storage.
 */
export function buildDocStoragePathSet(
  storagePaths: Iterable<string> | undefined | null,
): Set<string> {
  const set = new Set<string>();
  for (const path of storagePaths ?? []) {
    const normalized = normalizeDocStoragePath(path);
    if (normalized) set.add(normalized.toLowerCase());
  }
  return set;
}

/**
 * Unity `IsDocUsedInProject` / `IsDocEntryUsedInProject`. The customer check
 * is deliberately permissive on BOTH sides being blank — a manifest written
 * before `CustomerId` was recorded still resolves.
 */
export function isDocUsedInProject(
  entry: DocsManifestEntryDto | null | undefined,
  projectId: string,
  customerId?: string,
): boolean {
  if (!entry?.UsedInProjects || !projectId) return false;
  for (const ref of entry.UsedInProjects) {
    if (!ref?.ProjectId) continue;
    // Unity compares project ids with `StringComparison.Ordinal` here, but
    // `OrdinalIgnoreCase` in the delete paths. The web uses case-insensitive
    // matching throughout (project ids are lowercase GUIDs, so this can only
    // ever match MORE, never less) — see the header note on `upsertDocEntry`.
    if (ref.ProjectId.toLowerCase() !== projectId.toLowerCase()) continue;
    if (!customerId || !ref.CustomerId) return true;
    if (ref.CustomerId.toLowerCase() === customerId.toLowerCase()) return true;
  }
  return false;
}

/** Unity `AddUsedInProject` — idempotent insert. Mutates `usedInProjects`. */
export function addUsedInProject(
  usedInProjects: DocsProjectRefDto[],
  projectId: string,
  customerId?: string,
): void {
  if (!projectId) return;
  if (isDocUsedInProject({ UsedInProjects: usedInProjects }, projectId, customerId)) return;
  usedInProjects.push({ ProjectId: projectId, CustomerId: customerId ?? "" });
}

/** Unity `RemoveUsedInProject`. Mutates `usedInProjects`. */
export function removeUsedInProject(
  usedInProjects: DocsProjectRefDto[],
  projectId: string,
  customerId?: string,
): void {
  if (!projectId) return;
  for (let i = usedInProjects.length - 1; i >= 0; i--) {
    const ref = usedInProjects[i];
    if (!ref?.ProjectId) continue;
    if (ref.ProjectId.toLowerCase() !== projectId.toLowerCase()) continue;
    if (customerId && ref.CustomerId && ref.CustomerId.toLowerCase() !== customerId.toLowerCase()) {
      continue;
    }
    usedInProjects.splice(i, 1);
  }
}

/**
 * Unity `UpsertDocManifestEntry` — replace by `StoragePath`, else append.
 *
 * **Deliberate deviation:** Unity matches with `==` (ordinal) here and with
 * `OrdinalIgnoreCase` in every delete path, so re-uploading `Report.PDF` over
 * `report.pdf` appends a SECOND entry that the delete path then treats as the
 * same doc. The web matches case-insensitively everywhere, which is strictly
 * safer and stays read-compatible with Unity (it stores the literal path).
 */
export function upsertDocEntry(
  manifest: DocsManifestDto,
  entry: DocsManifestEntryDto,
): void {
  if (!manifest.Docs) manifest.Docs = [];
  const storagePath = normalizeDocStoragePath(entry.StoragePath);
  if (!storagePath) return;
  const key = storagePath.toLowerCase();
  const index = manifest.Docs.findIndex(
    (d) => normalizeDocStoragePath(d?.StoragePath)?.toLowerCase() === key,
  );
  if (index >= 0) manifest.Docs[index] = entry;
  else manifest.Docs.push(entry);
}

/** Find a manifest entry by storage path (case-insensitive, see above). */
export function findDocEntry(
  manifest: DocsManifestDto | null | undefined,
  storagePath: string,
): DocsManifestEntryDto | null {
  const key = normalizeDocStoragePath(storagePath)?.toLowerCase();
  if (!key || !manifest?.Docs) return null;
  return (
    manifest.Docs.find(
      (d) => normalizeDocStoragePath(d?.StoragePath)?.toLowerCase() === key,
    ) ?? null
  );
}

/** Remove every entry matching `storagePath` (case-insensitive). */
export function removeDocEntry(manifest: DocsManifestDto, storagePath: string): boolean {
  const key = normalizeDocStoragePath(storagePath)?.toLowerCase();
  if (!key || !manifest.Docs) return false;
  const before = manifest.Docs.length;
  manifest.Docs = manifest.Docs.filter(
    (d) => normalizeDocStoragePath(d?.StoragePath)?.toLowerCase() !== key,
  );
  return manifest.Docs.length !== before;
}

/**
 * Parse a manifest. Never throws — an unreadable manifest reads as EMPTY,
 * matching Unity's `catch { manifest = null; }` (`KnowledgeTransport.cs:1030-1037`).
 * Entries missing `FileName` or `StoragePath` are dropped, also Unity parity
 * (`:1045-1050`).
 */
export function parseDocsManifest(json: string | null | undefined): DocsManifestDto {
  if (!json?.trim()) return { Version: 1, Docs: [] };
  let parsed: unknown;
  try {
    parsed = JSON.parse(json);
  } catch {
    console.warn("[docsManifest] manifest is not valid JSON — treating as empty");
    return { Version: 1, Docs: [] };
  }
  if (typeof parsed !== "object" || parsed === null || Array.isArray(parsed)) {
    return { Version: 1, Docs: [] };
  }
  const manifest = parsed as DocsManifestDto;
  const docs = Array.isArray(manifest.Docs) ? manifest.Docs : [];
  manifest.Docs = docs.filter(
    (d): d is DocsManifestEntryDto =>
      !!d && typeof d === "object" && !!normalizeDocStoragePath(d.StoragePath) && !!d.FileName?.trim(),
  );
  if (typeof manifest.Version !== "number") manifest.Version = 1;
  return manifest;
}

export function serializeDocsManifest(manifest: DocsManifestDto): string {
  return JSON.stringify({ ...manifest, Docs: manifest.Docs ?? [] });
}

/**
 * `LastModifiedUtc` → epoch ms; 0 when absent or unparseable, which sorts the
 * row last (Unity's `DateTime.MinValue` fallback in `BuildDocItems`).
 */
export function parseDocLastModified(value: string | undefined): number {
  if (!value) return 0;
  const parsed = Date.parse(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

/**
 * The `LastModifiedUtc` shape Unity writes:
 * `DateTime.UtcNow.ToString(CultureInfo.InvariantCulture)` = `"MM/dd/yyyy HH:mm:ss"`.
 * Written verbatim so a Unity client parses a web-uploaded doc's timestamp
 * with its own `TryParse(..., InvariantCulture)` — an ISO string would parse
 * there too, but only by luck of the invariant parser's flexibility.
 */
export function formatDocLastModifiedUtc(date: Date): string {
  const pad = (n: number): string => String(n).padStart(2, "0");
  return (
    `${pad(date.getUTCMonth() + 1)}/${pad(date.getUTCDate())}/${date.getUTCFullYear()} ` +
    `${pad(date.getUTCHours())}:${pad(date.getUTCMinutes())}:${pad(date.getUTCSeconds())}`
  );
}

/** `"report.pdf"` → `"PDF"`; Unity `BuildDocItems`'s `Format` derivation. */
export function docFormat(fileName: string | undefined): string {
  const ext = fileExtension(fileName);
  return ext ? ext.toUpperCase() : "DOC";
}

/** Lower-cased extension WITHOUT the dot, or `""`. */
export function fileExtension(fileName: string | undefined): string {
  if (!fileName) return "";
  const dot = fileName.lastIndexOf(".");
  if (dot < 0 || dot === fileName.length - 1) return "";
  return fileName.slice(dot + 1).toLowerCase();
}

/** Unity `IsPdfFileName`. */
export function isPdfFileName(fileName: string | undefined): boolean {
  return fileExtension(fileName) === "pdf";
}

/** Unity `IsPdfNameOrPath` — true when either the name or the path ends `.pdf`. */
export function isPdfNameOrPath(value: string | undefined): boolean {
  return isPdfFileName(value?.trim());
}

/**
 * Best-effort `Content-Type` for a doc upload. S3 stores whatever we send;
 * this only matters for the browser when a citation is opened in a tab
 * (`application/pdf` renders inline, `application/octet-stream` downloads).
 * Unity's `PutFile` sets nothing and S3 defaults to `binary/octet-stream`.
 */
export function docContentType(fileName: string | undefined): string {
  switch (fileExtension(fileName)) {
    case "pdf":
      return "application/pdf";
    case "txt":
    case "md":
    case "rtf":
    case "tex":
    case "sh":
      return "text/plain; charset=utf-8";
    case "csv":
      return "text/csv; charset=utf-8";
    case "html":
      return "text/html; charset=utf-8";
    case "json":
      return "application/json; charset=utf-8";
    case "css":
      return "text/css; charset=utf-8";
    case "js":
    case "ts":
      return "text/javascript; charset=utf-8";
    case "doc":
      return "application/msword";
    case "docx":
      return "application/vnd.openxmlformats-officedocument.wordprocessingml.document";
    case "ppt":
      return "application/vnd.ms-powerpoint";
    case "pptx":
      return "application/vnd.openxmlformats-officedocument.presentationml.presentation";
    case "xls":
      return "application/vnd.ms-excel";
    case "xlsx":
      return "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
    default:
      return "application/octet-stream";
  }
}
